import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApplicationStateService } from '@core/service';
import { API_URL } from '@core/constants';
import {
  IAuthorizationDTO,
  IHcpcsDTO,
  IPds,
  IPdsCreateDTO,
  IPdsUpdate,
  IProductLineCreateDTO,
  IProductLineDTO
} from '@core/model';
import { AuthorizationStateService } from '@app/feature/authorization-details/authorization-state.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private get tenantId() {
    return this.applicationStateService.tenantId
  };

  private get baseUrl(): string {
    return `${this.apiUrl}/api/tenants/${this.tenantId}`;
  }

  constructor(
    public http: HttpClient,
    private applicationStateService: ApplicationStateService,
    private authorizationStateService: AuthorizationStateService,
    @Inject(API_URL) private apiUrl: string
  ) {
  }

  /**
   * Find HCPCS by code or description.
   * @description Permission: Public
   */
  findByCode(code: string): Observable<IHcpcsDTO[]> {
    const memberId= this.applicationStateService.memberId
    return this.http.get<IHcpcsDTO[]>(`${this.baseUrl}/productLineHcpcs/members/${memberId}/hcpcs`, {
      params: {
        codeOrDescriptionSearchText: code,
      },
    });
  }

  /**
   * Create product line
   * @description Permission: DMEPOSAuthorization.Update
   */
  create(authorizationId: string, payload: IProductLineCreateDTO): Observable<IProductLineDTO> {
    return this.http.post<IProductLineDTO>(`${this.serviceUrl(authorizationId)}`, payload)
  }

  /**
   * Update product line
   * @param authorizationId
   * @param id - product line id
   * @param payload
   * @description Permission: DMEPOSAuthorization.Update
   */
  update(authorizationId: string, id: string, payload: IProductLineDTO): Observable<IProductLineDTO> {
    return this.http.put<IProductLineDTO>(`${this.serviceUrl(authorizationId)}/${id}`, payload)
  }

  /**
   * Update product line
   * @param authorizationId
   * @param id - product line id
   * @param payload
   * @description Permission: DMEPOSAuthorization.Update
   */
  updateWithAdverseDetermination(authorizationId: string, id: string, payload: IProductLineDTO): Observable<IProductLineDTO> {
    return this.http.put<IProductLineDTO>(`${this.serviceUrl(authorizationId)}/${id}/withAdverseDetermination`, payload)
  }

  /**
   * Delete product line
   * @description Permission: DMEPOSAuthorization.Update
   */
  delete(authorizationId: string, productLineId: string): Observable<unknown> {
    return this.http.delete<unknown>(`${this.serviceUrl(authorizationId)}/${productLineId}`)
  }

  /**
   * Get product line form
   * @description Permission: DMEPOSAuthorization.Read
   */
  getForm(authorizationId: string, productLineId: string): Observable<object> {
    return this.http.get<object>(`${this.serviceUrl(authorizationId)}/${productLineId}/Questionnaires/form`)
  }

  /**
   * Get form submission
   * @description Permission: DMEPOSAuthorization.Read
   */
  getSubmission(authorizationId: string, productLineId: string): Observable<object> {
    return this.http.get<object>(`${this.serviceUrl(authorizationId)}/${productLineId}/Questionnaires`)
  }

  /**
   * Create or update the questionnaire
   * @description Permission: DMEPOSAuthorization.Update
   */
  sendSubmission(authorizationId: string, productLineId: string, payload: object): Observable<string> {
    return this.http.put<string>(`${this.serviceUrl(authorizationId)}/${productLineId}/Questionnaires`, payload)
  }

  /**
   * Resubmit Cases
   * @param authorizationId
   * @param ids - Product Case ids
   * @description Permission: DMEPOSAuthorization.Update
   */
  resubmit(authorizationId: string, ids: string[]): Observable<void> {
    return this.http.post<void>(`${this.productCasesAPIUrl(authorizationId)}/resubmit`, ids)
  }

  /**
   * Do Next Product Line
   * @description Permission: DMEPOSAuthorizationCase.DoNext
   */
  doNext(authorizationId: string, caseId: string, doNextKey: string): Observable<unknown> {
    return this.http.post<IProductLineDTO>(`${this.productCasesAPIUrl(authorizationId)}/${caseId}/doNext/${doNextKey}`, {})
  }

   /**
   * Merge Cases
   * @param authorizationId
   * @param ids - Product Case ids
   * @description Permission: DMEPOSAuthorization.Update
   */
   mergeCases(authorizationId: string, ids: string[]): Observable<void> {
    return this.http.post<void>(`${this.productCasesAPIUrl(authorizationId)}/merge`, ids)
  }

  /**
   * Read Product Line PDS
   * @description Permission: DMEPOSPostDecisionSupport.Read
   */
  getPds(productLineId: string, id: string): Observable<IPds[]> {
    return this.http.get<IPds[]>(`${this.productUrl(productLineId)}/${id}/pds`);
  }

  /**
   * Save PDS item
   * @param productLineId
   * @param id - product line id
   * @param payload
   * @description Permission: DMEPOSPostDecisionSupport.Create
   */
  savePds(productLineId: string, id: string, payload: IPdsCreateDTO) {
    return this.http.post(`${this.productUrl(productLineId)}/${id}/pds`, payload);
  }

  /**
   * Update PDS item
   * @param productLineId
   * @param id - product line id
   * @param payload
   * @description Permission: DMEPOSPostDecisionSupport.Update
   */
  updatePds(productLineId: string, id: string, payload: IPdsUpdate) {
    return this.http.put(`${this.productUrl(productLineId)}/${id}/pds`, payload);
  }

  /**
   * Get Suggested/Recommended Effective To
   * @param effectiveFrom
   * @param hcpcsByModifierId - GUID
   * @param unitsRequested
   * @description Permission: DMEPOSAuthorization.Update;
   * when modifier RR and unitsRequested equal 0, the method will return null
   */
  getRecommendedEffectiveTo(effectiveFrom: string, hcpcsByModifierId: string, unitsRequested: number): Observable<string | null> {
    const memberId= this.applicationStateService.memberId
    return this.http.get<string>(`${this.baseUrl}/productLines/members/${memberId}/recommendedEffectiveTo`, {
      params: {
        effectiveFrom,
        hcpcsByModifierId,
        unitsRequested,
      }
    });
  }

  movePL(authorizationId: string, lineId: string, newCaseId: string): Observable<IAuthorizationDTO>{
    return this.http.put<IAuthorizationDTO>(`${this.serviceUrl(authorizationId)}/${lineId}/moveToCase/${newCaseId}`, {})
  }

  dismissPL(authorizationId: string, id: string, payload: IProductLineDTO): Observable<IProductLineDTO> {
    return this.http.put<IProductLineDTO>(`${this.serviceUrl(authorizationId)}/${id}/dismiss`, payload)
  }

  duplicatePL(authorizationId: string, lineId: string): Observable<IAuthorizationDTO> {
    return this.http.post<IAuthorizationDTO>(`${this.serviceUrl(authorizationId)}/${lineId}/duplicate`, {})
  }

  private productUrl(id: string): string {
    return `${this.baseUrl}/authorizations/${id}/productLine`;
  }

  private serviceUrl(id: string): string {
    return `${this.baseUrl}/authorizations/${id}/productLines`;
  }

  private productCasesAPIUrl(authorizationByProductId: string): string {
    return `${this.baseUrl}/authorizations/${authorizationByProductId}/cases`;
  }
}
